import { Loader } from '../Widgets';
import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { AppSettings, Employee, EmployeeFaceDescriptor } from 'service/Models';
import ServerGateway from 'service/ServerGateway';
import Webcam from "react-webcam";
import toast, { Toaster } from 'react-hot-toast';
import SettingsService from 'service/SettingsService';

import * as faceapi from '@vladmandic/face-api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFaceSmile, faLocation, faLocationPin, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { NavLink, useNavigate } from 'react-router-dom';
import { Server } from 'http';
import { start } from 'repl';
const { DrawBox } = faceapi.draw;

export const PunchAttendance = () => {
    interface faceRecognitionStatus {
        type: string;
        message: string;
    }
    const MINIMUM_DETECTION_TIME = 5; // 3 seconds
    const user = useSelector((state: RootState) => state.appdata);
    const [employee, setEmployee] = useState<Employee>();
    const [locations, setLocations] = useState<any[]>([]);
    const [gatepasses, setGatepasses] = useState<any[]>([]);
    const [location, setLocation] = useState<any>();
    const [currentCoordinate, setCurrentCoordinate] = useState<any>();
    const [distance, setDistance] = useState<any>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isGatepassSaving, setIsGatepassSaving] = useState<boolean>(false);
    const [captureVideo, setCaptureVideo] = useState(false);
    const [settings, setSettings] = useState<AppSettings>();
    const [faceMatcher, setFaceMatcher] = useState<faceapi.FaceMatcher | null>(null);
    const [isFaceDataLoaded, setIsFaceDataLoaded] = useState<boolean>(false);
    const [isFaceRegistered, setIsFaceRegistered] = useState<boolean>(false);
    const [faceRecognitionStatus, setFaceRecognitionStatus] = useState<faceRecognitionStatus>({
        type: "bg-secondary",
        message: "Face recognition not started"
    });
    const [address, setAddress] = useState<string>("");
    const navigate = useNavigate();

    const faceDetectedStartTime = useRef<number | null>(null);
    const hasPunchedAttendance = useRef<boolean>(false);

    const webcamRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        return () => {
            if (webcamRef.current && webcamRef.current.srcObject) {
                const stream = webcamRef.current.srcObject as MediaStream;
                // Stop all tracks
                stream.getTracks().forEach(track => track.stop());
                // Remove stream from video element
                webcamRef.current.srcObject = null;
            }
        };
    }, []);

    let geoLocationOptions = {
        enableHighAccuracy: false,
        timeout: 30000,
        maximumAge: 0,
    };

    useEffect(() => {
        if (user.profile) {
            setEmployee(user.profile.profile);
        }
    }, [user]);

    useEffect(() => {
        if (employee) {
            // If settings are not found in session storage, fetch them from the server
            SettingsService.get()
                .then((data) => {
                    setSettings(data);
                });
        }
    }, [employee]);

    useEffect(() => {
        if (!settings) return;

        initPage();
    }, [settings]);

    const initPage = async () => {
        if (!settings) return;

        if (settings.trackLocation || settings.enableGeofence) {
            getLocations();
        }
        else if (settings.enableFaceDetection) {
            loadEmployeeFace();
        }
        else if (settings.takePicture) {
            startVideo();
        }
    }

    const startVideo = () => {
        console.log("Starting video");
        navigator.mediaDevices
            .getUserMedia({ video: { width: 300 } })
            .then(stream => {
                let video = webcamRef.current as unknown as HTMLVideoElement;
                if (video) {
                    video.srcObject = stream;
                    video.play();

                    if (canvasRef.current) {
                        canvasRef.current.height = video.clientHeight;
                        canvasRef.current.width = video.clientWidth;
                    }
                }
            })
            .catch(err => {
                console.error("error:", err);
            });
    }


    useEffect(() => {
        getGatepasses();
    }, [employee]);

    const loadFaceModels = async () => {
        const MODEL_URL = process.env.PUBLIC_URL + '/models';
        await Promise.all([
            faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
            faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
            faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
        ]);
        setIsLoading(false);
        startVideo();
    }

    useEffect(() => {
        setCurrentCoordinate(null);
        setAddress("");
        setDistance(0);
        navigator.geolocation.getCurrentPosition(function (position) {
            if (position && position.coords) {
                setCurrentCoordinate(position.coords);
            }
        }, function () {
            toast("Unable to get current location, please check if location permission is granted", { position: 'bottom-center', className: 'btn bg-danger' });
        }, { timeout: 30000 });

        navigator.geolocation.watchPosition(onChangePosition, function () {
            toast("Unable to get current location, please check if location permission is granted", { position: 'bottom-center', className: 'btn bg-danger' });
        }, geoLocationOptions);
    }, []);

    const onChangePosition = (position) => {
        if (position && position.coords) {
            setCurrentCoordinate(position.coords);
        }
    }

    useEffect(() => {
        if (!currentCoordinate || currentCoordinate.latitude === 0 || currentCoordinate.longitude === 0) {
            return;
        }

        if (location && currentCoordinate) {
            let distance = calculateDistance(location.lattitude, location.longitude, currentCoordinate.latitude, currentCoordinate.longitude).toFixed(2);
            setDistance(distance);

            if (settings && settings.enableGeofence && distance > location.radius) {
                toast("You are out of the geofence boundry, you cannot punch attendance", { position: 'bottom-center', className: 'btn bg-danger' });
                return;
            }
        }

        // If face detection is enabled and face matcher is not loaded, load the face matcher
        // Incase only picture is to be taken, start the video
        if (settings && !faceMatcher) {
            if (settings.enableFaceDetection) {
                loadEmployeeFace();
            }
            else if (settings.takePicture) {
                startVideo();
            }
        }

        if (!address || address === "") {
            setAddress("");
            if (!currentCoordinate) return;

            let api_key = process.env.REACT_APP_OLA_API_URL;
            if (api_key && api_key.length > 0) {
                let url = `https://api.olamaps.io/places/v1/reverse-geocode?latlng=${currentCoordinate.latitude},${currentCoordinate.longitude}&api_key=${api_key}`;
                fetch(url)
                    .then(response => response.json())
                    .then(data => {
                        if (data && data.results && data.results.length > 0) {
                            setAddress(data.results[0].formatted_address);
                        }
                    })
                    .catch(error => {
                        console.log("Error fetching location data:", error);
                    });
            }
        }
    }, [location, currentCoordinate]);

    const getLocations = () => {
        setIsLoading(true);
        if(!employee) return;

        if(employee.canLoginFromAnyLocation == true) {
        ServerGateway
            .getLocations()
            .then((data) => {
                setLocations(data);
                if (data.length > 0) {
                    setLocation(data[0]);
                }
                setIsLoading(false);
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
            });
        } else {
            ServerGateway
            .getEmployeeLocations(employee.id)
            .then((data) => {
                setLocations(data);
                if (data.length > 0) {
                    setLocation(data[0]);
                }
                setIsLoading(false);
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
            });
        }
    }

    const handleVideoOnPlay = () => {
        setCaptureVideo(true);
    }

    let displaySize = {
        width: 0,
        height: 0,
    }

    useEffect(() => {
        if (!captureVideo) return;

        console.log("Starting the face detection loop");
        if (!webcamRef.current) {
            console.log("Webcam not ready");
            return;
        }

        displaySize = {
            width: (webcamRef.current) ? webcamRef.current.clientWidth : 0,
            height: (webcamRef.current) ? webcamRef.current.clientHeight : 0,
        }

        // Create a canvas element for face-api.js on top of the video
        if (canvasRef && canvasRef.current) {
            const canvas = faceapi.createCanvasFromMedia(webcamRef.current);
            if (canvas && canvasRef.current) {
                canvasRef.current.appendChild(canvas);
            }
        }

        runDetectionLoop(); // Start the loop
    }, [captureVideo]);

    const loadEmployeeFace = async () => {
        if (!employee) return;

        try {
            const faceDescriptors: EmployeeFaceDescriptor[] = await ServerGateway.getEmployeeFaceDescriptor(employee.id);            
            setIsFaceDataLoaded(true);
            if (!faceDescriptors) {
                console.error("No face descriptors found");
                toast.error("No face registration found, please register your face");
                return;
            }
            let labeledDescriptors: faceapi.LabeledFaceDescriptors[] = [];
            faceDescriptors.forEach((payload: EmployeeFaceDescriptor) => {
                if (typeof payload.faceDescriptors === "string") {
                    // Parse as array of arrays or nulls
                    const parsedData: (number[] | null)[] = JSON.parse(payload.faceDescriptors);

                    // Convert each array of numbers to Float32Array, filter out nulls
                    const float32Arrays: Float32Array[] = parsedData
                        .filter((desc): desc is number[] => desc !== null)
                        .map((desc) => new Float32Array(desc));

                    // Now safely create labeled descriptor with no nulls
                    let employee = new faceapi.LabeledFaceDescriptors(payload.name, float32Arrays);
                    labeledDescriptors.push(employee);
                }
            });

            if (labeledDescriptors.length > 0) {
                setIsFaceRegistered(true);
                setFaceMatcher(new faceapi.FaceMatcher(labeledDescriptors, 0.6));
                console.log("Setting the face matcher");
            }
        } catch (error) {
            console.error("Error loading employee face descriptors:", error);
            toast.error("Error loading face data, please try again");
        }
    }

    useEffect(() => {
        if (!faceMatcher) return;

        loadFaceModels();
    }, [faceMatcher]);

    const wait = (ms) => {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    const runDetectionLoop = async () => {
        await detectFace();
        if (webcamRef.current) {
            setTimeout(runDetectionLoop, 1000); // Wait 1000ms after detectFace finishes
        }
    }

    const detectFace = async () => {
        if (!webcamRef.current) {
            console.log("Detection loop started, but webcam not ready");
            return;
        }
        if (canvasRef.current) {
            const canvas: HTMLCanvasElement = canvasRef.current;
            const context: CanvasRenderingContext2D = canvas.getContext('2d') as CanvasRenderingContext2D;
            if (context) {
                context.clearRect(0, 0, displaySize.width, displaySize.height);
            }
        }

        const options = new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.6 });
        const detection = await faceapi.detectSingleFace(webcamRef.current, options).withFaceLandmarks().withFaceDescriptor();
        if (!detection) {
            setFaceRecognitionStatus({ type: "bg-secondary", message: "No face detected" });
            console.log("No face detected");
            faceDetectedStartTime.current = null;          // Reset timer
            hasPunchedAttendance.current = false;          // Allow punch again after loss of detection
            return;
        }

        setFaceRecognitionStatus({ type: "bg-primary", message: "Face Detected, matching in progress..." });
        const resizedDetection = faceapi.resizeResults(detection, displaySize);
        {/*if (canvasRef.current) {
            faceapi.draw.drawDetections(canvasRef.current, resizedDetection);
        }*/}

        const box = resizedDetection.detection.box;
        const drawBox = new DrawBox(box, { label: '', boxColor: 'green', lineWidth: 1 });
        const ctx = canvasRef?.current?.getContext('2d');
        if (ctx) {
            drawBox.draw(ctx);
        }

        // let payload = JSON.stringify(Array.from(detection.descriptor));
        if (faceMatcher) {
            const bestMatch = faceMatcher.findBestMatch(detection.descriptor);
            if (bestMatch.label !== 'unknown') {
                console.log("Face matched to " + bestMatch.label + ", Distance = " + bestMatch.distance);
                setFaceRecognitionStatus({ type: "bg-success", message: "Face matched to " + bestMatch.label });

                const now = Date.now();
                if (faceDetectedStartTime.current === null) {
                    faceDetectedStartTime.current = now;  // Start timer at first detection
                } else {
                    const elapsedSeconds = (now - faceDetectedStartTime.current) / 1000;
                    if (elapsedSeconds >= MINIMUM_DETECTION_TIME && !hasPunchedAttendance.current) {
                        hasPunchedAttendance.current = true; // Prevent repeated calls
                        console.log("Face detected continuously for 3 seconds, punching attendance...");
                        punchAttendance();
                    }
                    else {
                        setFaceRecognitionStatus({ type: "bg-info", message: `Face matched, hold still for ${MINIMUM_DETECTION_TIME - Math.floor(elapsedSeconds)} more seconds` });
                    }
                }
            }
            else {
                setFaceRecognitionStatus({ type: "bg-danger", message: "Face not matched" });
                await wait(2000);
            }
        }
        else {
            setFaceRecognitionStatus({ type: "bg-secondary", message: "Face matcher not loaded" });
        }

        {/*
            // THE SERVER DETECTION MODE
            let response = await axios.post('${process.env.REACT_APP_API_URL}/api/master/employee/face/match', payload);
            if (response) {
                console.log("Response = ");
                console.log(response.data);
                if (response.data && response.data.label != "unknown") {
                    alert("Face matched to " + response.data.label + ", Distance = " + response.data.distance);
                }
                else {
                    alert("Face not matched");
                }
            }
        */}
    }

    const getGatepasses = () => {
        if (employee?.id && employee?.id > 0) {
            ServerGateway
                .getGatepass(employee?.id, "0")
                .then((data) => {
                    setGatepasses(data.entries);
                })
                .catch(error => {
                    console.log(error);
                });
        }
    }

    function calculateDistance(lat1, lon1, lat2, lon2) {
        if ((lat1 == lat2) && (lon1 == lon2)) {
            return 0;
        }
        else {
            var radlat1 = Math.PI * lat1 / 180;
            var radlat2 = Math.PI * lat2 / 180;
            var theta = lon1 - lon2;
            var radtheta = Math.PI * theta / 180;
            var dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
            if (dist > 1) {
                dist = 1;
            }
            dist = Math.acos(dist);
            dist = dist * 180 / Math.PI;
            dist = dist * 60 * 1.1515;
            dist = dist * 1.609344;
            return dist * 1000;
        }
    }

    const punchAttendance = () => {
        if (gatepasses.length > 0) {
            let lastGatepassTime = moment(gatepasses[0].issuedAt);
            let currentTime = moment();
            let diff = currentTime.diff(lastGatepassTime, 'minutes');
            if (diff < 5) {
                faceDetectedStartTime.current = null;          // Reset timer
                hasPunchedAttendance.current = false;          // Allow punch again after loss of detection

                toast('Please wait for 5 minute before punching again', { position: 'bottom-center', className: 'btn bg-danger' });
                return;
            }
        }

        try {
            let remarks: any = {};
            if (settings?.trackLocation) {
                if (!currentCoordinate) {
                    faceDetectedStartTime.current = null;          // Reset timer
                    hasPunchedAttendance.current = false;          // Allow punch again after loss of detection

                    toast("Unable to get the current location, Try again later", { position: 'bottom-center', className: 'btn bg-danger' });
                    return;
                }

                if (locations && locations.length > 0) {
                    remarks = {
                        lat: currentCoordinate.latitude,
                        long: currentCoordinate.longitude,
                        location: location.name,
                        address: address,
                        distance: distance + "m"
                    };
                }
                else {
                    remarks = {
                        lat: currentCoordinate.latitude,
                        long: currentCoordinate.longitude,
                        address: address
                    };
                }
            }

            if (settings?.takePicture) {
                if (!webcamRef.current) {
                    const imageSrc = (webcamRef.current as any).getScreenshot();
                    remarks.imageSrc = imageSrc;
                }
            }

            let gatepass = {
                gatepassType: 'App',
                employeeId: employee?.id,
                issuedAt: moment().format("YYYY-MM-DDTHH:mm:ss"),
                remarks: JSON.stringify(remarks)
            };
            setIsGatepassSaving(true);
            ServerGateway
                .postGatepass(gatepass)
                .then((data) => {
                    setIsGatepassSaving(false);
                    toast('Your punch information saved successfully', { position: 'bottom-center', className: 'badge bg-success text-white font-weight-bold' });

                    faceDetectedStartTime.current = null;          // Reset timer
                    hasPunchedAttendance.current = false;          // Allow punch again after loss of detection

                    wait(5000);
                    navigate("/attendance");
                })
                .catch(error => {
                    console.log(error);
                    toast('Error in saving punch information, please try again', { position: 'bottom-center', className: 'btn bg-danger' });

                    faceDetectedStartTime.current = null;          // Reset timer
                    hasPunchedAttendance.current = false;          // Allow punch again after loss of detection

                    setIsGatepassSaving(false);
                });
        }
        catch (error) {
            console.log(error);
            toast('Error in saving punch information, please try again', { position: 'bottom-center', className: 'btn bg-danger' });

            faceDetectedStartTime.current = null;          // Reset timer
            hasPunchedAttendance.current = false;          // Allow punch again after loss of detection

            setIsGatepassSaving(false);
        }
    }

    const isFaceDetectionEnabled = settings?.enableFaceDetection;
    const isFaceDetectionDisabled = !settings?.enableFaceDetection;
    const canShowAttendanceButton = (locations.length === 0
        || (locations.length > 0 && location)) &&
        (!settings?.takePicture || captureVideo);

    {/* Check if the current location is within the geofence, Set to true if geo-fencing is not enabled,
        or if the location is not set
        or if the distance is within the location radius */}
    const isWithinGeofence = locations.length === 0 ||
        !settings?.enableGeofence ||
        (distance !== 0 && distance < location.radius);

    const gatepassCount = gatepasses.length;
    const isPunchIn = gatepassCount === 0 || (gatepassCount > 0 && gatepassCount % 2 === 0);

    return (
        <div className="page-mark-attendance">
            {isLoading &&
                <Loader />
            }
            {!isLoading &&
                <>
                    <div><Toaster /></div>
                    {settings?.enableGeofence && locations && locations.length > 0 &&
                        <div className='row'>
                            <div className="form-group">
                                <label htmlFor="leavetype">Office\Location</label>
                                <select className="form-control" id="leavetype" value={location?.id} onChange={(e) => {
                                    let loc = locations.find(l => l.id == parseInt(e.target.value));
                                    setLocation(loc);
                                }}>
                                    {locations?.map((l) => (
                                        <option value={l.id} key={l.id}>{l.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    }
                    {/* Face detection is disabled or face matcher (i.e Face is already registered) is available, then show the camera */}
                    {(isFaceDetectionDisabled || faceMatcher) &&
                        <div className='row mb-2'>
                            <div className='col-md-12'>
                                {(settings?.takePicture || settings?.enableFaceDetection) &&
                                    <div className='card my-2 p-0'>
                                        <div className='card-body p-0'>
                                            <div className='card-img-top camera-container'>
                                                <video ref={webcamRef} onLoadedData={handleVideoOnPlay} className='camera-video' />
                                                <canvas ref={canvasRef} className='camera-overlay' />
                                            </div>
                                        </div>

                                        <div className='card-footer text-center'>
                                            {isGatepassSaving &&
                                                <div className="spinner-border text-light" role="status" style={{ height: "1.2rem", width: "1.2rem" }}>
                                                    <span className="sr-only">Saving...</span>
                                                </div>
                                            }
                                            {!isGatepassSaving &&
                                                <>
                                                    {isFaceDetectionEnabled &&
                                                        <div className={`badge ${faceRecognitionStatus.type} text-white`}>
                                                            {faceRecognitionStatus.message}
                                                        </div>
                                                    }
                                                    {isFaceDetectionDisabled && canShowAttendanceButton && isWithinGeofence && (
                                                        <div className={isPunchIn ? 'btn bg-success text-white w-100' : 'btn bg-danger text-white w-100'} onClick={punchAttendance}>
                                                            {isPunchIn ? <span>Punch In</span> : <span>Punch Out</span>}
                                                        </div>
                                                    )}
                                                </>
                                            }
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    }

                    {settings?.trackLocation &&
                        <div className='row my-2'>
                            <div className='col-md-12'>
                                <div className='card'>
                                    <div className="card-header">
                                        <h5>
                                            {currentCoordinate &&
                                                <FontAwesomeIcon icon={faLocation} className="me-2" />
                                            }
                                            {!currentCoordinate &&
                                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                            }
                                            Current
                                            Location
                                        </h5>
                                    </div>
                                    <div className="card-body" style={{ fontSize: "0.8rem" }}>
                                        Latitude: {currentCoordinate?.latitude}<br />
                                        Longitude: {currentCoordinate?.longitude}<br />
                                        {address && <><span>Address: {address}</span><br /></>}
                                        {locations && locations.length > 0 && location &&
                                            <>
                                                <span>Distance: {distance} meters</span>
                                                {(distance == 0 || distance > location.radius) &&
                                                    <div className='badge bg-danger'>Out of boundry</div>
                                                }
                                                {(distance > 0 && distance < location.radius) &&
                                                    <div className='badge bg-success'>Within boundry</div>
                                                }
                                            </>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    <div className='row my-2'>
                        <div className="col-md-12">
                            <ul className="list-group list-group-flush">
                                {gatepasses && gatepasses.map((g) => (
                                    <li className="list-group-item">
                                        {moment(g.issuedAt).format("hh:mm a")}
                                        <div className={g.isLogin ? 'badge bg-success' : 'badge bg-danger'} style={{ float: "right" }}>
                                            {g.isLogin ? "IN" : "OUT"}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {(isFaceDetectionEnabled && isFaceDataLoaded && !isFaceRegistered) && (
                        <div className='row my-2'>
                            <div className="col-md-12">
                                <NavLink className="btn btn-success w-100" to="/face/register">Register Face</NavLink>
                            </div>
                        </div>
                    )}
                </>
            }
        </div>
    )
};